class EuroCaseTransform extends HTMLElement {
  connectedCallback() {
    this.swiperEl = this.querySelector('[data-case-transform-swiper]');
    this.dotsEl = this.querySelector('[data-case-transform-dots]');
    this.prevEl = this.querySelector('[data-case-transform-prev]');
    this.nextEl = this.querySelector('[data-case-transform-next]');
    this.activeCompare = null;
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('keydown', this.onKeyDown);
    this.whenReady(() => this.initSwiper());
  }

  disconnectedCallback() {
    clearInterval(this.readyTimer);
    this.unbindDrag();
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('keydown', this.onKeyDown);
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  whenReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }

    this.readyTimer = setInterval(() => {
      if (!window.Swiper) return;
      clearInterval(this.readyTimer);
      callback();
    }, 30);
  }

  initSwiper() {
    if (!this.swiperEl || !window.Swiper) return;

    const slides = this.swiperEl.querySelectorAll('.swiper-slide');
    if (!slides.length) return;

    this.swiper = new window.Swiper(this.swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 450,
      watchOverflow: true,
      allowTouchMove: false,
      simulateTouch: false,
      loop: slides.length > 1,
      navigation: {
        prevEl: this.prevEl,
        nextEl: this.nextEl,
      },
      pagination:
        this.dotsEl && slides.length > 1
          ? {
              el: this.dotsEl,
              clickable: false,
              bulletClass: 'euro-case-transform__dot',
              bulletActiveClass: 'is-active',
            }
          : undefined,
      on: {
        init: () => {
          window.EuroAOS?.refresh();
        },
        slideChange: (swiper) => {
          const slide = swiper.slides?.[swiper.activeIndex];
          if (slide) window.EuroAOS?.replayIn(slide);
        },
      },
    });
  }

  onPointerDown(event) {
    const handle = event.target.closest('[data-case-transform-handle]');
    if (!handle || !this.contains(handle)) return;

    const compare = handle.closest('[data-case-transform-compare]');
    if (!compare) return;

    event.preventDefault();
    this.activeCompare = compare;
    handle.setPointerCapture?.(event.pointerId);
    this.setCompareFromClientX(compare, event.clientX);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.activeCompare) return;
    this.setCompareFromClientX(this.activeCompare, event.clientX);
  }

  onPointerUp() {
    this.unbindDrag();
  }

  unbindDrag() {
    this.activeCompare = null;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }

  onKeyDown(event) {
    const handle = event.target.closest('[data-case-transform-handle]');
    if (!handle || !this.contains(handle)) return;

    const compare = handle.closest('[data-case-transform-compare]');
    if (!compare) return;

    const step = event.shiftKey ? 10 : 2;
    let next = this.getCompareValue(compare);

    if (event.key === 'ArrowLeft' || event.key === 'Home') {
      event.preventDefault();
      next = event.key === 'Home' ? 0 : Math.max(0, next - step);
      this.setCompareValue(compare, next);
    } else if (event.key === 'ArrowRight' || event.key === 'End') {
      event.preventDefault();
      next = event.key === 'End' ? 100 : Math.min(100, next + step);
      this.setCompareValue(compare, next);
    }
  }

  getCompareValue(compare) {
    const raw = getComputedStyle(compare).getPropertyValue('--compare').trim();
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : 50;
  }

  setCompareFromClientX(compare, clientX) {
    const rect = compare.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = ((clientX - rect.left) / rect.width) * 100;
    this.setCompareValue(compare, ratio);
  }

  setCompareValue(compare, value) {
    const clamped = Math.min(100, Math.max(0, value));
    compare.style.setProperty('--compare', `${clamped}%`);
    const handle = compare.querySelector('[data-case-transform-handle]');
    if (handle) handle.setAttribute('aria-valuenow', String(Math.round(clamped)));
  }
}

customElements.define('euro-case-transform', EuroCaseTransform);
